import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useArticles } from "../../hooks/useArticles";
import { toNewsItems } from "../../lib/articleAdapter";
import { filterBySearch } from "../../lib/articleSearch";
import SEO from "@/components/SEO";
import { AdCintillo, AdSidebar } from "@/components/feature/SponsorAds";
import NewsletterPopup from "@/components/feature/NewsletterPopup";

export default function Aerolineas() {
  const [search, setSearch] = useState("");
  const { articles, loading } = useArticles("Aerolíneas", false);
  const newsItems = useMemo(() => toNewsItems(articles), [articles]);

  const filtered = useMemo(() => {
    const byId = new Map(articles.map((a) => [a.id, a]));
    return filterBySearch(newsItems, search, (item) => byId.get(item.id));
  }, [newsItems, articles, search]);

  return (
    <main className="min-h-screen bg-[#F4F7FB]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        jsonLdSchemas={[
          {
            "@type": "WebPage",
            name: "Aerolíneas",
            description: "Noticias, novedades y actualidad del sector aerolíneas en República Dominicana.",
            url: 'https://aviacioninsightrd.com/aerolineas',
          },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-[#0B1F3A] pt-32 pb-12">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-3">
            <Link to="/" className="text-blue-400 text-sm hover:text-blue-300">Inicio</Link>
            <i className="ri-arrow-right-s-line text-blue-600" />
            <span className="text-slate-400 text-sm">Aerolíneas</span>
          </div>
          <h1 className="text-white font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)" }}>
            AEROLÍNEAS
          </h1>
          <p className="text-blue-200/70 text-sm mt-2">
            Noticias, novedades y actualidad del sector aerolíneas en República Dominicana
          </p>
        </div>
      </section>

      <AdCintillo placement="Aerolíneas" className="bg-[#F4F7FB]" />

      {/* Search bar */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-6 pt-8">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar noticias de aerolíneas..."
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          />
        </div>
      </div>

      {/* Noticias */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-1 h-7 bg-red-600 rounded-full" />
            <h2 className="text-[#0B1F3A] font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.6rem" }}>
              ÚLTIMAS NOTICIAS · AEROLÍNEAS
            </h2>
          </div>
          <Link to="/actualidad?categoria=Aerolíneas" className="text-red-600 text-sm font-medium hover:underline hidden sm:flex items-center gap-1 whitespace-nowrap" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Ver todas <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-72 bg-slate-200 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((item) => (
                  <Link key={item.id} to={`/articulo/${item.slug}`} className="group block">
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-slate-300 transition-all">
                      <div className="h-48 overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <span className={`inline-block text-white text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full ${item.categoryColor}`}>
                          {item.category}
                        </span>
                        <h3 className="text-[#0B1F3A] font-semibold text-sm mt-2 line-clamp-2 group-hover:text-slate-600 transition-colors" style={{ fontFamily: "'Sora', sans-serif" }}>
                          {item.title}
                        </h3>
                        <p className="text-slate-400 text-xs mt-1">{item.date}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-search-line text-4xl text-slate-300 mb-3 block" />
                <p className="text-slate-400 text-sm">No se encontraron noticias.</p>
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="mt-3 text-red-600 text-sm font-medium hover:underline cursor-pointer"
                  >
                    Limpiar búsqueda
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Publicidad (columna derecha, igual que en Aeropuertos) */}
          <aside className="lg:col-span-1">
            <AdSidebar
              placement="Aerolíneas"
              placeholders={[
                { title: "Sector Aeronáutico", desc: "Llega a miles de lectores especializados", format: "mediano" },
                { title: "Turismo & Viajes", desc: "Conecta con viajeros del Caribe dominicano" },
              ]}
            />
          </aside>
        </div>
      </div>

      <NewsletterPopup />
      <Footer />
    </main>
  );
}